import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useLayoutEffect,
} from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CookieStorage,
} from 'amazon-cognito-identity-js';
import { COOKIES_DOMAIN, FORNECEDORES_API_URL } from 'src/constants/urls';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import { pushDataLayer } from 'src/utils/pushDataLayer';
import { tokenRegister } from 'src/services/api';
import { useRefreshSession } from './utils/useRefreshSession';
import { Rudderstack } from '../../analytics/rudderstack/events';

export const UserContext = createContext();

const {
  REACT_APP_COGNITO_USER_POOL_ID: COGNITO_USER_POOL_ID,
  REACT_APP_COGNITO_CLIENT_ID: COGNITO_CLIENT_ID,
} = process.env;

const cookieStorage = new CookieStorage({
  domain: COOKIES_DOMAIN,
});

const poolData = {
  UserPoolId: COGNITO_USER_POOL_ID,
  ClientId: COGNITO_CLIENT_ID,
  Storage: cookieStorage,
};

const signUpURL = `${FORNECEDORES_API_URL}/supplier/supplier`;
// TODO: Leave this in a url file.

export function UserProvider({ children }) {
  const [userPool, setUserPool] = useState();
  const [user, setUser] = useState();
  const [auth, setAuth] = useState(false);
  const [userData, setUserData] = useState();
  const [loading, setLoading] = useState(true);
  const [userSession, setUserSession] = useState(null);
  const [toast, setToast] = useState();
  const [userEmail, setUserEmail] = useState();

  const token = useMemo(() => userSession?.idToken.jwtToken, [userSession]);
  const accessToken = useMemo(
    () => userSession?.accessToken.jwtToken,
    [userSession],
  );

  useRefreshSession({
    user,
    userSession,
    onRefresh: setUserSession,
  });

  const sendEvent = useCallback(
    (event, payload = {}) => {
      const payloadData = {
        ...payload,
        event,
        userID: user?.username || '',
        username: userData?.Account_Name || userData?.razao_social || '',
        timestamp: new Date().toUTCString(),
      };
      pushDataLayer(event, payloadData);
    },
    [user?.username, userData?.Account_Name, userData?.razao_social],
  );

  useLayoutEffect(() => {
    if (!userData) return;

    const userName = userData?.Account_Name || userData?.razao_social;
    const showEmailOnFP = userData?.Mostrar_email_lojista_no_FP || '';
    sessionStorage.setItem('fornecedorName', userName);
    sessionStorage.setItem('fornecedorEmail', showEmailOnFP);
  }, [userData]);

  // TODO: replace with toastify library
  const showToast = useCallback((body, action) => {
    const actionColor = {
      success: '#4bb543',
      error: '#ce2525',
      information: '#0dcaf0',
      default: '#000000',
    };
    const currentColor = actionColor[action] || actionColor.default;
    setToast({ body, color: currentColor });
  }, []);

  const closeToast = () => {
    setToast(undefined);
  };

  useEffect(() => {
    if (auth) {
      sendEvent('user-login');
    }
  }, [auth, sendEvent]);

  useEffect(() => {
    const newUserPool = new CognitoUserPool(poolData);
    const user = newUserPool.getCurrentUser();
    if (user) {
      user.getSession(async (err, session) => {
        if (!err && session) {
          const raw = await fetch(`${FORNECEDORES_API_URL}/supplier/me`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: session.idToken.jwtToken,
            },
          });
          if (raw.status === 200) {
            const data = await raw.json();
            setUserData(data);
            setUserSession(session);
            setUser(user);
            setAuth(true);

            const userUUID = session?.idToken?.payload?.['custom:user_uuid'];
            const email = session?.idToken?.payload?.email;
            setUserEmail(email);
            Rudderstack.identify(userUUID, {
              name: data.Multivendor,
              email,
              userUUID,
            });

            window?.hj?.('identify', userUUID, { inventa_user_uuid: userUUID });
          } else {
            window.location.href = 'https://inventa.shop/account/login';
          }
        }

        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    setUserPool(newUserPool);
  }, []);

  useLayoutEffect(() => tokenRegister(token), [token]);

  const signIn = useCallback(
    (username, password, callback) => {
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });
      const userData = {
        Username: username,
        Pool: userPool,
        Storage: cookieStorage,
      };
      const user = new CognitoUser(userData);
      user.authenticateUser(authDetails, {
        onSuccess: result => {
          if (typeof callback === 'function') callback(result);
          setUser(user);
          window.location.replace('/');
        },
        onFailure: err => {
          if (typeof callback === 'function') callback(err);
        },
      });
    },
    [userPool],
  );

  const signOut = useCallback(() => {
    user?.signOut();

    window.location.replace('/login');
  }, [user]);

  const signUp = useCallback(async (data, callback) => {
    // TODO: move to a Service class
    const raw = await fetch(signUpURL, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const parsed = await raw.json();
    if (typeof callback === 'function') {
      if (raw.status === 201) callback({ ok: true });
      else callback({ ok: false, error: parsed.error });
    }
  }, []);

  const recoverPassword = useCallback(
    async (email, callback) => {
      const userData = {
        Username: email,
        Pool: userPool,
        Storage: cookieStorage,
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.forgotPassword({
        onSuccess: result => {
          if (typeof callback === 'function') callback({ ok: true, result });
        },
        onFailure: err => {
          if (typeof callback === 'function') callback({ ok: false, err });
        },
      });
      setUser(cognitoUser);
    },
    [userPool],
  );

  const sendCodeAndPassword = useCallback(
    async (code, password, callback, email) => {
      if (email === '' || !email) {
        callback({ ok: false, err: 'Ocorreu um erro, tente novamente' });
        return;
      }
      const newUserPool = new CognitoUserPool(poolData);
      const userData = {
        Username: email,
        Pool: newUserPool,
      };
      const user = new CognitoUser(userData);
      if (user) {
        user.confirmPassword(code, password, {
          onSuccess: result => {
            if (typeof callback === 'function') callback({ ok: true, result });
          },
          onFailure: err => {
            if (typeof callback === 'function') callback({ ok: false, err });
          },
        });
      }
    },
    [],
  );

  const sendLoginCode = useCallback(
    (email, callback) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
      });
      const user = new CognitoUser({
        Username: email,
        Pool: userPool,
        Storage: cookieStorage,
      });

      function customAuth() {
        return {
          onSuccess: () => {
            setAuth(true);
          },
          onFailure: err => {
            if (typeof callback === 'function') callback({ ok: false, err });
          },
          customChallenge: () => {
            if (typeof callback === 'function') {
              callback({
                ok: true,
                triggerFunction(code) {
                  if (code) user.sendCustomChallengeAnswer(code, customAuth());
                },
              });
            }
          },
        };
      }

      user.setAuthenticationFlowType('CUSTOM_AUTH');
      user.initiateAuth(authenticationDetails, customAuth());
    },
    [userPool],
  );

  const value = useMemo(
    () => ({
      auth,
      accessToken,
      signIn,
      signOut,
      signUp,
      recoverPassword,
      sendCodeAndPassword,
      sendLoginCode,
      showToast,
      token,
      loading,
      sendEvent,
      userData,
      userEmail,
    }),
    [
      auth,
      accessToken,
      signIn,
      signOut,
      signUp,
      recoverPassword,
      sendCodeAndPassword,
      sendLoginCode,
      showToast,
      token,
      loading,
      sendEvent,
      userData,
      userEmail,
    ],
  );

  return (
    <UserContext.Provider value={value}>
      {!loading && (
        <div className="container-fluid position-relative px-0">
          {children}
          {!!toast?.body && (
            <ToastContainer position="top-end">
              <Toast
                animation
                show={!!toast.body}
                onClose={closeToast}
                delay={6000}
                autohide
                className="m-4 fs-6"
                style={{ color: toast.color, borderColor: toast.color }}
              >
                <Toast.Body>{toast.body}</Toast.Body>
              </Toast>
            </ToastContainer>
          )}
        </div>
      )}
    </UserContext.Provider>
  );
}

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
